import { PropsWithChildren, useState } from 'react';
import { EditOutlined } from '@ant-design/icons';
import { Button, Input, Typography } from 'antd';
import styled, { CSSObject } from 'styled-components';
import { Row } from '@/shared/ui';
import { DEFAULT_PAGE_BANNER_COLOR } from './constants';
import { PageTitleBannerAvatarImage } from './PageTitleBannerAvatarImage';
import {
  PageTitleBannerAvatarUpload,
  PageTitleBannerAvatarUploadProps,
} from './PageTitleBannerAvatarUpload';

const { Title } = Typography;

type Props = PropsWithChildren<
  PageTitleBannerAvatarUploadProps & {
    bannerColor?: string;
    canEdit?: boolean;
    isEditMode?: boolean;
    onToggleMode?: (isEditMode: boolean) => void;
    onTitleUpdate?: (title: string) => void;
    bannerStyle?: CSSObject;
    contentStyle?: CSSObject;
  }
>;

export const PageTitleBanner = ({
  title = '',
  imageUrl,
  bannerColor,
  canEdit,
  isEditMode,
  isLoading,
  onAvatarUpload,
  onAvatarRemove,
  onTitleUpdate,
  onToggleMode,
  bannerStyle,
  contentStyle,
}: Props) => {
  const [newTitle, setNewTitle] = useState<string>(title);

  return (
    <>
      <BannerColorPanel bgColor={bannerColor} style={bannerStyle} />
      <Row alignItems="center" gap={20} padding={30} style={contentStyle}>
        {canEdit ? (
          <PageTitleBannerAvatarUpload
            imageUrl={imageUrl}
            title={title}
            isLoading={isLoading}
            onAvatarUpload={onAvatarUpload}
            onAvatarRemove={onAvatarRemove}
          />
        ) : (
          <PageTitleBannerAvatarImage imageUrl={imageUrl} title={title} />
        )}
        {isEditMode ? (
          <>
            <Input
              defaultValue={title}
              onChange={(e) => setNewTitle(e.target.value)}
              onPressEnter={() => onTitleUpdate?.(newTitle)}
              autoFocus
              style={{ width: '100%', maxWidth: 300 }}
            />
            <Row>
              <Button
                onClick={() => onToggleMode?.(false)}
                style={{ marginTop: 5 }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                loading={isLoading}
                onClick={() => onTitleUpdate?.(newTitle)}
                style={{ marginTop: 5 }}
              >
                Save
              </Button>
            </Row>
          </>
        ) : (
          <>
            <Title level={2} style={{ marginBottom: 0 }}>
              {title}
            </Title>
            {canEdit && (
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => onToggleMode?.(true)}
                style={{ marginTop: 5 }}
              >
                Rename
              </Button>
            )}
          </>
        )}
      </Row>
    </>
  );
};

export const BannerColorPanel = styled.div<{ bgColor?: string }>`
  height: 20%;
  background: ${({ bgColor = DEFAULT_PAGE_BANNER_COLOR }) => bgColor};
`;
